'use client'

import { useCallback, useMemo } from 'react'
import { useUser } from '@auth0/nextjs-auth0'
import { useSearchParams } from 'next/navigation'

import { useLocalStorage } from './useLocalStorage'
import { STORAGE_KEY } from '../constants'
import { useEffectiveLocalizeVersion } from './useEffectiveLocalizeVersion'

import { QUERY_PARAMS } from '@/app/constants'
import { getUnlocodeForVersion, getDefaultPort, getPortByUnlocode } from '@/app/config/ports'
import { useRouter, usePathname } from '@/i18n/navigation'

// Legacy tab type for backward compatibility (used by useVesselScrollPosition)
export type TabType = 'bookmarks' | 'arrival' | 'inPort' | 'lineUp' | 'followed'

// New two-level tab structure
export type TopLevelTabType = 'port' | 'lineUp' | 'followed'
export type PortSubTabType = 'arrival' | 'inPort' | 'lineUp' | 'followed'

export type TUseVesselTabStateOptions = {
  disableRedirectTab?: boolean
  portUnlocode?: string
}

export default function useVesselTabState(options: TUseVesselTabStateOptions = {}) {
  const { user } = useUser()
  const { disableRedirectTab = false, portUnlocode: explicitPortUnlocode } = options

  const router = useRouter()
  const pathname = usePathname()

  const isHomePage = pathname === '/' || pathname.match(/^\/[a-z]{2}$/)

  const localizeVersion = useEffectiveLocalizeVersion()
  const defaultPort = getDefaultPort()
  const inferredPortUnlocode = getUnlocodeForVersion(localizeVersion) ?? defaultPort.unlocode

  const portUnlocode = explicitPortUnlocode ?? inferredPortUnlocode

  // Derive port slug and name key from UNLOCODE using centralized config
  const portConfig = getPortByUnlocode(portUnlocode) ?? defaultPort
  const portSlug = portConfig.slug

  // Get the port name key for translations (e.g., 'SANTOS', 'HOUSTON')
  const portNameKey = portConfig.translationKey

  const searchParams = useSearchParams()
  // Check if we're on the lineup page route is /lineup or contains query param ?lineup=true
  const isLineupPage = useMemo(() => {
    return pathname.includes('/lineup') || searchParams.has(QUERY_PARAMS.LINEUP)
  }, [pathname, searchParams])

  // Derive top-level tab and sub-tab from path
  const getTabsFromPath = (): { topTab: TopLevelTabType; subTab: PortSubTabType } => {
    if (pathname.includes('/ships-expected')) {
      return { topTab: 'port', subTab: 'arrival' }
    } else if (pathname.includes('/ships-at-port')) {
      return { topTab: 'port', subTab: 'inPort' }
    } else if (isLineupPage) {
      return { topTab: 'lineUp', subTab: 'lineUp' }
    } else if (isHomePage) {
      // Homepage: either root "/" or locale-only paths like "/en", "/pt"
      return { topTab: 'port', subTab: 'arrival' }
    }
    return { topTab: 'port', subTab: 'arrival' }
  }

  // Legacy getTabFromPath for backward compatibility
  const getTabFromPath = (): TabType | undefined => {
    if (pathname.includes('/ships-expected')) {
      return 'arrival'
    } else if (pathname.includes('/ships-at-port')) {
      return 'inPort'
    } else if (isHomePage) {
      return 'bookmarks'
    }
    return undefined
  }

  // Store top-level tab
  const [storedTopTab, setStoredTopTab] = useLocalStorage<TopLevelTabType>(
    STORAGE_KEY.ACTIVE_TOP_TAB,
    'port'
  )

  // Store sub-tab for port (arrival or inPort)
  const [storedSubTab, setStoredSubTab] = useLocalStorage<PortSubTabType>(
    STORAGE_KEY.ACTIVE_PORT_SUB_TAB,
    'arrival'
  )

  // Derive active tabs based on path or stored values
  const pathTabs = getTabsFromPath()
  const baseTopTab = disableRedirectTab || isHomePage ? storedTopTab : pathTabs.topTab
  const activeTopTab: TopLevelTabType = !user && baseTopTab === 'lineUp' ? 'port' : baseTopTab
  const activeSubTab = disableRedirectTab || isHomePage ? storedSubTab : pathTabs.subTab

  // Legacy activeTab for backward compatibility (derived from path only)
  const activeTab = getTabFromPath()

  // Track if user has been initialized to prevent interference with later tab changes
  const [hasInitializedTab, setHasInitializedTab] = useLocalStorage<boolean>(
    STORAGE_KEY.HAS_INITIALIZED_TAB,
    false
  )

  // Handle top-level tab change
  const handleTopTabChange = useCallback(
    (value: string) => {
      const tabValue = value as TopLevelTabType
      setStoredTopTab(tabValue)

      // When switching to port tab, navigate based on current sub-tab
      if (!disableRedirectTab && tabValue === 'port') {
        if (storedSubTab === 'arrival') {
          router.push(`/${portSlug}/ships-expected`)
        } else {
          router.push(`/${portSlug}/ships-at-port`)
        }
      } else if (!disableRedirectTab && tabValue === 'followed') {
        router.push('/')
        setStoredSubTab('followed')
      } else if (!disableRedirectTab && tabValue === 'lineUp') {
        router.push('/lineup')
        setStoredSubTab('lineUp')
      }
    },
    [disableRedirectTab, portSlug, router, setStoredSubTab, setStoredTopTab, storedSubTab]
  )

  // Handle sub-tab change (for port tab)
  const handleSubTabChange = useCallback(
    (value: string) => {
      const tabValue = value as PortSubTabType
      setStoredSubTab(tabValue)

      if (!disableRedirectTab) {
        if (tabValue === 'arrival') {
          router.push(`/${portSlug}/ships-expected`)
        } else {
          router.push(`/${portSlug}/ships-at-port`)
        }
      }
    },
    [disableRedirectTab, portSlug, router, setStoredSubTab]
  )

  // Legacy handleTabChange for backward compatibility
  const handleTabChange = useCallback(
    (value: string) => {
      const tabValue = value as TabType

      // Map legacy tab to new structure
      if (tabValue === 'arrival' || tabValue === 'inPort') {
        setStoredTopTab('port')
        setStoredSubTab(tabValue)
      } else if (tabValue === 'bookmarks') {
        setStoredTopTab('followed')
      }

      if (disableRedirectTab === false) {
        if (tabValue === 'arrival') {
          router.push(`/${portSlug}/ships-expected`)
        } else if (tabValue === 'inPort') {
          router.push(`/${portSlug}/ships-at-port`)
        } else {
          router.push('/')
        }
      }
    },
    [disableRedirectTab, portSlug, router, setStoredTopTab, setStoredSubTab]
  )

  // Initialize tab for new users (set to port > arrival if no bookmarks)
  const initializeTabForNewUser = useCallback(
    (hasBookmarks: boolean, bookmarksLoading: boolean) => {
      if (isHomePage && !bookmarksLoading && !hasInitializedTab && !hasBookmarks) {
        setStoredTopTab('port')
        setStoredSubTab('arrival')
        setHasInitializedTab(true)
      }
    },
    [isHomePage, hasInitializedTab, setStoredTopTab, setStoredSubTab, setHasInitializedTab]
  )

  return {
    // New two-level tab API
    activeTopTab,
    activeSubTab,
    handleTopTabChange,
    handleSubTabChange,
    portNameKey,

    // Legacy API for backward compatibility
    activeTab,
    handleTabChange,

    // Common
    portUnlocode,
    portSlug,
    isHomePage,
    initializeTabForNewUser
  }
}
